import { Link, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useBasePath } from '~/common/hooks/useBasePath';
import { cn } from '~/common/utils/styleUtil';
import { LucideArrowRightLeft, LucideLoader2 } from 'lucide-react';

type CatalogArticleNotAvailableProps = {
  showCurrentlyNotAvailableMessage: boolean;
  showSellOffMessage: boolean;
  substituteArticleNumber?: string;
  substituteArticleText?: string;
  notInWebshop: boolean;
  className?: string;
  onSwapArticle?: () => void;
};

export function CatalogArticleNotAvailable({
  showCurrentlyNotAvailableMessage,
  showSellOffMessage,
  substituteArticleNumber,
  substituteArticleText,
  notInWebshop,
  className,
  onSwapArticle
}: Readonly<CatalogArticleNotAvailableProps>) {
  const { t } = useTranslation();
  const { path } = useBasePath();
  const navigation = useNavigation();
  const isSwapPending =
    onSwapArticle &&
    navigation.state === 'submitting' &&
    navigation.formData?.get('intent') === 'changeArticle';

  let text = t('Der Artikel ist nicht mehr verfügbar');
  if (showCurrentlyNotAvailableMessage) {
    text = t('Der Artikel ist aktuell nicht verfügbar');
  }
  if (showSellOffMessage) {
    text = t('Der Artikel ist zurzeit eingeschränkt verfügbar.');
  }
  if (notInWebshop) {
    text = t('Im Webshop nicht bestellbar');
  }

  return (
    <div
      className={cn(
        'border rounded w-full p-2  text-sm flex flex-wrap gap-1 flex-col desktop:flex-row',
        !showCurrentlyNotAvailableMessage && !showSellOffMessage && !notInWebshop && 'bg-primary text-white',
        (showCurrentlyNotAvailableMessage || notInWebshop) && 'border-primary',
        showSellOffMessage && 'border-orange-500',
        className
      )}
      data-testid={'article-not-available-text'}
    >
      <p className={'font-bold'}>{text}</p>
      {substituteArticleNumber && (
        <div className={'flex flex-wrap items-center'}>
          <span>{t('Ersatzartikel')}:</span>&nbsp;
          <Link
            className={'underline'}
            to={`${path}/catalog/article/${substituteArticleNumber}`}
            data-testid={'substitute-article-link'}
          >
            <p className={'line-clamp-1'}>
              {substituteArticleNumber}&nbsp;{substituteArticleText}
            </p>
          </Link>
          {onSwapArticle && (
            <button
              type={'button'}
              className={cn(
                'ml-2 flex items-center gap-1 underline cursor-pointer hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed',
                !showCurrentlyNotAvailableMessage && !showSellOffMessage && !notInWebshop
                  ? 'text-white'
                  : 'text-primary'
              )}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onSwapArticle();
              }}
              disabled={!!isSwapPending}
              data-testid={'swap-substitute-article-button'}
            >
              {isSwapPending ? <LucideLoader2 size={14} className="animate-spin" /> : <LucideArrowRightLeft size={14} />}
              <span>{t('Austauschen')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
